import { Box, Button, Flex, Grid, IconButton, Input, Text } from '@chakra-ui/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiArrowUpTray, HiXMark } from 'react-icons/hi2';
import { useSourcesHost } from '../context/SourcesHostContext';
import {
  parseCleartouchPostman,
  ParsedCleartouchPostman,
} from '../utils/parseCleartouchPostman';

export interface PortalAlertTypeConfig {
  alertTypeId: number;
  commandPortalEnabled: boolean;
  alertStartToken: string;
  alertFileId: string;
  alertStopToken: string;
  drillStartToken: string;
  drillFileId: string;
  drillStopToken: string;
}

interface CleartouchPortalConfigProps {
  alertTypes: { id: number; name: string }[];
  config: PortalAlertTypeConfig[];
  onChange: (config: PortalAlertTypeConfig[]) => void;
  /** Bubble site-level values (schoolCode, appKey, deviceGroups) parsed from
   *  an imported Postman file up to the parent form. */
  onSiteImport?: (site: ParsedCleartouchPostman['site']) => void;
}

const emptyConfig = (alertTypeId: number): PortalAlertTypeConfig => ({
  alertTypeId,
  commandPortalEnabled: true,
  alertStartToken: '',
  alertFileId: '',
  alertStopToken: '',
  drillStartToken: '',
  drillFileId: '',
  drillStopToken: '',
});

/**
 * Per-alert-type Command Portal configuration for Cleartouch sources.
 *
 * Shows a card for each selected alert type with token and file ID fields.
 */
const CleartouchPortalConfig = ({
  alertTypes,
  config,
  onChange,
  onSiteImport,
}: CleartouchPortalConfigProps) => {
  const { t } = useTranslation();
  const { toaster } = useSourcesHost();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const getConfig = (alertTypeId: number) =>
    config.find((c) => c.alertTypeId === alertTypeId) ??
    emptyConfig(alertTypeId);

  const updateConfig = (
    alertTypeId: number,
    update: Partial<PortalAlertTypeConfig>,
  ) => {
    const exists = config.find((c) => c.alertTypeId === alertTypeId);
    if (exists) {
      onChange(
        config.map((c) =>
          c.alertTypeId === alertTypeId ? { ...c, ...update } : c,
        ),
      );
    } else {
      onChange([...config, { ...emptyConfig(alertTypeId), ...update }]);
    }
  };

  const removeConfig = (alertTypeId: number) => {
    onChange(config.filter((c) => c.alertTypeId !== alertTypeId));
  };

  const handleImport = async (
    alertTypeId: number,
    side: 'alert' | 'drill',
    file: File,
  ) => {
    try {
      const raw = await file.text();
      const parsed = parseCleartouchPostman(raw);
      const update: Partial<PortalAlertTypeConfig> = {
        alertTypeId,
        commandPortalEnabled: true,
      };
      if (side === 'alert') {
        if (parsed.tokens.startToken) update.alertStartToken = parsed.tokens.startToken;
        if (parsed.tokens.stopToken) update.alertStopToken = parsed.tokens.stopToken;
        if (parsed.tokens.fileId) update.alertFileId = parsed.tokens.fileId;
      } else {
        if (parsed.tokens.startToken) update.drillStartToken = parsed.tokens.startToken;
        if (parsed.tokens.stopToken) update.drillStopToken = parsed.tokens.stopToken;
        if (parsed.tokens.fileId) update.drillFileId = parsed.tokens.fileId;
      }
      updateConfig(alertTypeId, update);
      onSiteImport?.(parsed.site);
      if (parsed.warnings.length) {
        toaster.create({
          type: 'warning',
          title: t('imported_from_postman_with_warnings'),
          description: parsed.warnings.join('\n'),
        });
      } else {
        toaster.create({
          type: 'success',
          title: t('imported_from_postman'),
          description: t('tokens_plus_file_id_populated', { side: side === 'alert' ? t('alert') : t('drill') }),
        });
      }
    } catch (err) {
      toaster.create({
        type: 'error',
        title: t('could_not_import_file'),
        description: err instanceof Error ? err.message : t('unknown_error'),
      });
    }
  };

  const renderImportButton = (
    alertTypeId: number,
    side: 'alert' | 'drill',
  ) => {
    const refKey = `${alertTypeId}-${side}`;
    return (
      <>
        <input
          ref={(el) => {
            fileInputRefs.current[refKey] = el;
          }}
          type="file"
          accept=".json,application/json"
          hidden
          aria-label={t('import_postman_json_aria', { side: side === 'alert' ? t('alert') : t('drill') })}
          title={t('import_postman_json_aria', { side: side === 'alert' ? t('alert') : t('drill') })}
          data-e2e={`portal-${side}-import-input-${alertTypeId}`}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImport(alertTypeId, side, file);
            e.target.value = '';
          }}
        />
        <Button
          size="xs"
          variant="outline"
          onClick={() => fileInputRefs.current[refKey]?.click()}
          data-e2e={`portal-${side}-import-${alertTypeId}`}
        >
          <HiArrowUpTray />
          {t('import_postman_json')}
        </Button>
      </>
    );
  };

  if (alertTypes.length === 0) {
    return (
      <Text fontSize="13px" color="text.tertiary" p="4px">
        {t('select_alert_types_above_to_configure_command_portal_settings')}
      </Text>
    );
  }

  return (
    <Grid gap="12px">
      {alertTypes.map((at) => {
        const cfg = getConfig(at.id);
        return (
          <Box
            key={at.id}
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="lg"
            p="12px"
            data-e2e={`portal-config-${at.id}`}
          >
            <Flex justify="space-between" align="center" mb="8px">
              <Text fontSize="14px" fontWeight={600} color="text.primary">
                {at.name}
              </Text>
              <IconButton
                aria-label={t('remove_configuration')}
                size="xs"
                variant="ghost"
                color="text.tertiary"
                onClick={() => removeConfig(at.id)}
                data-e2e={`portal-config-remove-${at.id}`}
              >
                <HiXMark />
              </IconButton>
            </Flex>

            <Grid gap="12px">
                {/* Alert Section */}
                <Box>
                  <Flex justify="space-between" align="center" mb="6px">
                    <Text
                      fontSize="13px"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {t('alert')}
                    </Text>
                    {renderImportButton(at.id, 'alert')}
                  </Flex>
                  <Grid templateColumns="1fr 1fr" gap="8px" mb="8px">
                    <PortalField
                      label={t('start_token')}
                      help={t('cleartouch_start_token_alert_help')}
                      value={cfg.alertStartToken}
                      onChange={(v) =>
                        updateConfig(at.id, {
                          alertTypeId: at.id,
                          alertStartToken: v,
                        })
                      }
                      e2e={`portal-alert-start-${at.id}`}
                    />
                    <PortalField
                      label={t('stop_token')}
                      help={t('cleartouch_stop_token_alert_help')}
                      value={cfg.alertStopToken}
                      onChange={(v) =>
                        updateConfig(at.id, {
                          alertTypeId: at.id,
                          alertStopToken: v,
                        })
                      }
                      e2e={`portal-alert-stop-${at.id}`}
                    />
                  </Grid>
                  <PortalField
                    label={t('file_id')}
                    help={t('cleartouch_file_id_alert_help')}
                    value={cfg.alertFileId}
                    onChange={(v) =>
                      updateConfig(at.id, {
                        alertTypeId: at.id,
                        alertFileId: v,
                      })
                    }
                    e2e={`portal-alert-file-${at.id}`}
                  />
                </Box>

                {/* Drill Section */}
                <Box>
                  <Flex justify="space-between" align="center" mb="6px">
                    <Text
                      fontSize="13px"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {t('drill')}
                    </Text>
                    {renderImportButton(at.id, 'drill')}
                  </Flex>
                  <Grid templateColumns="1fr 1fr" gap="8px" mb="8px">
                    <PortalField
                      label={t('start_token')}
                      help={t('cleartouch_start_token_drill_help')}
                      value={cfg.drillStartToken}
                      onChange={(v) =>
                        updateConfig(at.id, {
                          alertTypeId: at.id,
                          drillStartToken: v,
                        })
                      }
                      e2e={`portal-drill-start-${at.id}`}
                    />
                    <PortalField
                      label={t('stop_token')}
                      help={t('cleartouch_stop_token_drill_help')}
                      value={cfg.drillStopToken}
                      onChange={(v) =>
                        updateConfig(at.id, {
                          alertTypeId: at.id,
                          drillStopToken: v,
                        })
                      }
                      e2e={`portal-drill-stop-${at.id}`}
                    />
                  </Grid>
                  <PortalField
                    label={t('file_id')}
                    help={t('cleartouch_file_id_drill_help')}
                    value={cfg.drillFileId}
                    onChange={(v) =>
                      updateConfig(at.id, {
                        alertTypeId: at.id,
                        drillFileId: v,
                      })
                    }
                    e2e={`portal-drill-file-${at.id}`}
                  />
                </Box>
              </Grid>
          </Box>
        );
      })}
    </Grid>
  );
};

/** Small labeled input for a portal token/file ID */
const PortalField = ({
  label,
  help,
  value,
  onChange,
  e2e,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (v: string) => void;
  e2e: string;
}) => (
  <Box>
    <Text fontSize="13px" fontWeight={500} color="text.primary" mb="4px">
      {label}
    </Text>
    <Input
      placeholder={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="sm"
      fontSize="13px"
      bg="surface.primary"
      color="text.secondary"
      borderColor="border.secondary"
      borderRadius="md"
      data-e2e={e2e}
    />
    <Text color="text.tertiary" fontSize="11px" mt="2px">
      {help}
    </Text>
  </Box>
);

export default CleartouchPortalConfig;
